import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  ThemeProvider,
  createTheme,
  Alert,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import documentEditorService from '../../services/documentEditor.service';
import { useProject } from '../../context/ProjectContext';

// Enterprise theme
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  typography: { fontFamily: '"Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif' },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } } },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 2px 4px rgba(0,0,0,0.05)' } } },
  },
});

const SAPSectionHistory = () => {
  const { id: sectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();

  // Determine the actual projectId to use
  const actualProjectId = currentProject?._id || projectIdFromUrl;

  const [section, setSection] = useState(null);
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      console.log('SAPSectionHistory: Loading project from URL:', projectIdFromUrl);
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  useEffect(() => {
    if (sectionId && actualProjectId && !projectContextLoading) {
      fetchSectionAndVersions();
    } else if (!sectionId && !actualProjectId && !projectContextLoading) {
      setError('No section ID or project selected.');
      setIsLoading(false);
    } else if (sectionId && !actualProjectId && !projectContextLoading) {
      setError('Please select a project to view its SAP section history.');
      setIsLoading(false);
    }
  }, [sectionId, actualProjectId, projectContextLoading]);

  const fetchSectionAndVersions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch section details - ensure projectId is passed
      const sectionData = await documentEditorService.getSAPSection(sectionId, actualProjectId);
      setSection(sectionData.document || sectionData);

      // Fetch version history - ensure projectId is passed
      const versionsData = await documentEditorService.getAllSAPVersions(sectionId, actualProjectId);
      setVersions(versionsData.versions || []);
    } catch (err) {
      console.error('Error fetching SAP section history:', err);
      setError(err.message || 'Failed to load SAP section history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    // Navigate back to SAP editor, preserving the projectId
    navigate(`/document-editor/sap/${sectionId}?projectId=${actualProjectId}`);
  };

  if (isLoading || projectContextLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (error || projectContextError) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3 }}>
          <Typography color="error">{error || projectContextError}</Typography>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Editor
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  if (!section || !sectionId || !actualProjectId) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3 }}>
          <Typography color="warning">No section or project selected for history.</Typography>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Editor
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBack}
          sx={{ mb: 3 }}
        >
          Back to Editor
        </Button>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {section?.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            SAP Section History
          </Typography>
        </Paper>

        <List>
          {versions.length > 0 ? (versions.map((version, index) => (
            <React.Fragment key={version._id}>
              <ListItem>
                <ListItemText
                  primary={`Version ${version.versionNumber}`}
                  secondary={
                    <React.Fragment>
                      <Typography component="span" variant="body2" color="text.secondary" display="block" gutterBottom>
                        {new Date(version.createdAt).toLocaleString()}
                      </Typography>
                      {version.prompt && (
                        <Typography component="span" variant="body2" display="block" sx={{ mt: 1 }}>
                          <strong>Prompt:</strong> {version.prompt}
                        </Typography>
                      )}
                      {version.output && (
                        <Box sx={{ mt: 1 }}>
                          <Typography component="span" variant="body2" display="block" sx={{ mb: 0.5 }}>
                            <strong>Content:</strong>
                          </Typography>
                          <Box
                            sx={{
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              p: 1,
                              fontSize: 14,
                              lineHeight: 1.5,
                              bgcolor: '#fafafa',
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(version.output) }}
                          />
                        </Box>
                      )}
                    </React.Fragment>
                  }
                />
              </ListItem>
              {index < versions.length - 1 && <Divider />}
            </React.Fragment>
          ))) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No version history available for this SAP section.
            </Typography>
          )}
        </List>
      </Box>
    </ThemeProvider>
  );
};

export default SAPSectionHistory; 